import React, { useState } from 'react'
import type { GroupResult } from '../../types'
import { getUIStrings } from '../../i18n/ui'
import { resolveColor } from '../../styles/colorContrast'
import { totalPoints, formatPoints } from '../../utils/score'

interface GroupedBreakdownProps {
  groups: GroupResult[]
  overallPercentage?: number
  lang?: string
}

const GroupedBreakdown: React.FC<GroupedBreakdownProps> = ({ groups, overallPercentage, lang }) => {
  const t = getUIStrings(lang)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const toggle = (groupId: string) => {
    setExpanded(prev => ({ ...prev, [groupId]: !prev[groupId] }))
  }

  return (
    <div className="w-full max-w-3xl mx-auto space-y-4">
      {overallPercentage != null && (
        <div className="text-center mb-4">
          <span className="text-3xl font-bold text-primary-600" data-testid="overall-points">
            {formatPoints(totalPoints(groups))}
          </span>
          <span className="ml-2 text-foreground-muted">{t.points}</span>
        </div>
      )}

      {groups.map((group) => {
        const hex = resolveColor(group.color, '#6B7280')
        const score = Math.round(group.percentage)
        const isOpen = expanded[group.group_id] ?? false

        return (
          <div key={group.group_id} className="border border-border rounded-xl overflow-hidden">
            {/* Group header — clickable */}
            <button
              onClick={() => toggle(group.group_id)}
              className="w-full flex items-center justify-between px-3 sm:px-5 py-3 sm:py-4 bg-background-alt hover:bg-gray-50 transition-colors gap-2"
            >
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-3 h-3 sm:w-4 sm:h-4 rounded flex-shrink-0" style={{ backgroundColor: hex }} />
                <span className="font-semibold text-foreground text-sm sm:text-base truncate">{group.group_name}</span>
                <span className="text-xs sm:text-sm text-foreground-muted hidden sm:inline">
                  ({t.questionCount(group.question_scores.length)})
                </span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <span className="text-base sm:text-lg font-bold" style={{ color: hex }}>{score}%</span>
                <svg
                  className={`w-4 h-4 sm:w-5 sm:h-5 text-foreground-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {/* Group progress bar */}
            <div className="px-5 pb-1">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${score}%`, backgroundColor: hex }}
                />
              </div>
            </div>

            {/* Expanded: per-question details */}
            {isOpen && (
              <div className="px-5 py-3 space-y-3 border-t border-border">
                {group.question_scores.map((qs) => {
                  const qPct = Math.round(qs.percentage)
                  return (
                    <div key={qs.question_id}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-foreground-muted">{qs.category}</span>
                        <span className="font-medium text-foreground">
                          {qs.score.toFixed(0)} / {qs.max_possible_score.toFixed(0)} ({qPct}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{ width: `${qPct}%`, backgroundColor: hex }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default GroupedBreakdown
